import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { setSessionLostHandler } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";

const AuthContext = createContext(null);
const RESUME_CHECK_MS = 60_000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = logged out
  const [sessionEnded, setSessionEnded] = useState(false);
  const userRef = useRef(user);
  const lastCheck = useRef(0);
  userRef.current = user;

  const load = useCallback(async () => {
    lastCheck.current = Date.now();
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    setSessionLostHandler((reason) => {
      const wasSignedIn = !!userRef.current;
      queryClient.clear();
      setUser(null);
      if (wasSignedIn) {
        setSessionEnded(true);
        toast.error(reason === "suspended" ? "This account has been suspended." : "Your session has ended. Please sign in again.");
      }
    });
    load();
  }, [load]);

  useEffect(() => {
    const check = () => {
      if (document.visibilityState !== "visible" || !userRef.current) return;
      if (Date.now() - lastCheck.current < RESUME_CHECK_MS) return;
      lastCheck.current = Date.now();
      api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => {});
    };
    const onPageShow = (e) => { if (e.persisted) { lastCheck.current = 0; check(); } };
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    window.addEventListener("online", check);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
      window.removeEventListener("online", check);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  const register = async (email, password) => {
    const { data } = await api.post("/auth/register", { email, password });
    setSessionEnded(false);
    setUser(data);
    return data;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    queryClient.clear();
    setSessionEnded(false);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      queryClient.clear();
      setUser(null);
    }
  };

  const refreshUser = load;

  return (
    <AuthContext.Provider value={{ user, sessionEnded, register, login, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
